/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Papa from 'papaparse';
import type {
    BuildingStatisticsResponse,
    ConfigurationReadResponse,
    ConfigurationTags,
    ConfigurationWriteResponse,
    IntervalValueRequest,
    IntervalValueResponse,
    LastValueRequest,
    LastValueResponse,
    ApiTokenCredentials,
    ApiTokenResponse,
    StoredChartJsRequest,
    StoredChartJsResponse,
    StoredPlotlyRequest,
    StoredPlotlyResponse,
    TimeSeriesRequest,
    TimeSeriesResponse,
} from '@/types/eeCheck';
import {
    buildEeCheckApiCacheKey,
    getOrCreateEeCheckApiInFlightRequest,
    readEeCheckApiCache,
    writeEeCheckApiCache,
} from '@/api/support/eeCheckApiCache';
import { encodeEeCheckTimeSeriesUrn } from '@/api/support/eeCheckUrl';

const MAX_PLOTLY_CACHE_SIZE_BYTES = 50 * 1024;
const MAX_TIME_SERIES_URN_BATCH_BYTES = 3500;
const IS_INTERNAL_API_PROXY_ENABLED = import.meta.env.DEV;
const INTERNAL_TOKEN_LOGIN_URL = '/internal/auth/token-login';
const INTERNAL_STORED_CHARTJS_URL = '/internal/api/stored-chartjs-by-definition-name';
const INTERNAL_STORED_PLOTLY_URL = '/internal/api/stored-plotly-by-tags';
const INTERNAL_TIME_SERIES_URL = '/internal/api/data-series';
const INTERNAL_LAST_VALUE_URL = '/internal/api/current-values';
const INTERNAL_INTERVAL_VALUE_URL = '/internal/api/interval-values';
const INTERNAL_MUENSTER_TIME_SERIES_URL = '/internal/api/muenster-time-series';
const INTERNAL_CONFIGURATION_READ_URL = '/internal/api/configuration/read';
const INTERNAL_CONFIGURATION_WRITE_URL = '/internal/api/configuration/write';
const INTERNAL_BUILDING_STATISTICS_URL = '/internal/api/building-statistics';
const DIRECT_TOKEN_LOGIN_PATH = '/api/token-login';
const DIRECT_STORED_CHARTJS_PATH = '/api/meter_charts/stored_chartjs_by_definition_name';
const DIRECT_STORED_PLOTLY_PATH = '/api/meter_charts/stored_plotly_by_tags';
const DIRECT_TIME_SERIES_PATH_PREFIX = '/api/data/series/csv/timestamp';
const DIRECT_LAST_VALUE_PATH = '/api/data/current_values/csv/timestamp';
const DIRECT_INTERVAL_VALUE_PATH_PREFIX = '/api/data/interval_diff/csv/timestamp';
const DIRECT_CONFIGURATION_READ_PATH = '/api/data/get_file_by_tags';
const DIRECT_CONFIGURATION_WRITE_PATH = '/api/data/upload_file_by_tags';
const DIRECT_BUILDING_STATISTICS_PATH_PREFIX = '/api/data/building_statistics';
const EE_CHECK_API_ORIGIN = 'https://app.enobserve.de';
const DIRECT_API_ORIGIN = __EE_CHECK_API_ORIGIN__;
const LAST_VALUE_REQUEST_OPTIONS = {
    'additional-times': false,
    'include-formatted-time': false,
} as const;

export interface EeCheckApiRequestProgress {
    currentRequest: number;
    totalRequests: number;
}

export interface EeCheckApiMeta {
    method: string
    rawResponse: string
    status: number
    url: string
}

export interface EeCheckApiResult<T> {
    data: T
    meta: EeCheckApiMeta
}

export interface EeCheckApiOriginalRequest {
    headers?: Record<string, string>
    method: 'GET' | 'POST'
    parameters: unknown
    url: string
}

export class EeCheckApiError extends Error {
    rawResponse: string;
    status: number;
    url: string;

    constructor(message: string, status: number, url: string, rawResponse: string) {
        super(message);
        this.name = 'EeCheckApiError';
        this.rawResponse = rawResponse;
        this.status = status;
        this.url = url;
    }
}

function getEffectiveCredentials(
    credentials?: ApiTokenCredentials,
): ApiTokenCredentials | null {
    if (credentials) {
        return credentials;
    }

    if (__DEFAULT_API_USER__ && __DEFAULT_API_PASSWORD__) {
        return {
            password: __DEFAULT_API_PASSWORD__,
            username: __DEFAULT_API_USER__,
        };
    }

    return null;
}

function getSerializedSizeBytes(value: unknown): number {
    return new Blob([JSON.stringify(value)]).size;
}

function splitUrnBatches(urn: string, maxBytes: number): string[] {
    const urnParts = urn
        .split(',')
        .map((currentUrn) => currentUrn.trim())
        .filter(Boolean);
    const batches: string[] = [];
    let currentBatch: string[] = [];

    urnParts.forEach((currentUrn) => {
        const nextBatch = [...currentBatch, currentUrn];
        const nextBatchSize = new Blob([encodeURIComponent(nextBatch.join(','))]).size;

        if (currentBatch.length > 0 && nextBatchSize > maxBytes) {
            batches.push(currentBatch.join(','));
            currentBatch = [currentUrn];
            return;
        }

        currentBatch = nextBatch;
    });

    if (currentBatch.length > 0) {
        batches.push(currentBatch.join(','));
    }

    return batches;
}

interface ParsedCsvResult {
    fields: string[];
    rows: Record<string, string>[];
}

function parseTimeSeriesCsvResponse(csv: string): ParsedCsvResult {
    const parseResult = Papa.parse<Record<string, string>>(csv, {
        delimiter: ';',
        header: true,
        skipEmptyLines: true,
    });

    return {
        fields: parseResult.meta.fields ?? [],
        rows: parseResult.data,
    };
}

function mergeTimeSeriesCsvResponses(csvResponses: string[]): string {
    if (csvResponses.length === 0) {
        return '';
    }

    if (csvResponses.length === 1) {
        return csvResponses[0];
    }

    const [firstCsv, ...remainingCsvResponses] = csvResponses;
    const firstParsed = parseTimeSeriesCsvResponse(firstCsv);
    const mergedFields = [...firstParsed.fields];
    const mergedRows = firstParsed.rows.map((row) => ({ ...row }));
    const rowKeyField = ['local server time', 'timestamp'].find((field) =>
        mergedFields.includes(field),
    ) ?? null;

    remainingCsvResponses.forEach((currentCsv) => {
        const parsedCsv = parseTimeSeriesCsvResponse(currentCsv);

        parsedCsv.fields.forEach((field) => {
            if (!mergedFields.includes(field)) {
                mergedFields.push(field);
            }
        });

        if (rowKeyField) {
            const rowLookup = new Map<string, Record<string, string>>();

            parsedCsv.rows.forEach((row) => {
                const rowKey = row[rowKeyField];

                if (rowKey) {
                    rowLookup.set(rowKey, row);
                }
            });

            mergedRows.forEach((row, index) => {
                const rowKey = row[rowKeyField];
                const sourceRow = (rowKey && rowLookup.get(rowKey)) ?? parsedCsv.rows[index];

                if (sourceRow) {
                    Object.assign(row, sourceRow);
                }
            });

            return;
        }

        mergedRows.forEach((row, index) => {
            const sourceRow = parsedCsv.rows[index];

            if (sourceRow) {
                Object.assign(row, sourceRow);
            }
        });
    });

    return Papa.unparse({
        fields: mergedFields,
        data: mergedRows.map((row) => mergedFields.map((field) => row[field] ?? '')),
    }, {
        delimiter: ';',
    });
}

function mergeLastValueCsvResponses(csvResponses: string[]): string {
    if (csvResponses.length === 0) {
        return '';
    }

    if (csvResponses.length === 1) {
        return csvResponses[0];
    }

    const [firstCsv, ...remainingCsvResponses] = csvResponses;
    const firstParsed = parseTimeSeriesCsvResponse(firstCsv);
    const mergedRows = [...firstParsed.rows];

    remainingCsvResponses.forEach((currentCsv) => {
        const parsedCsv = parseTimeSeriesCsvResponse(currentCsv);

        mergedRows.push(...parsedCsv.rows);
    });

    return Papa.unparse({
        fields: firstParsed.fields,
        data: mergedRows.map((row) => firstParsed.fields.map((field) => row[field] ?? '')),
    }, {
        delimiter: ';',
    });
}

function extractErrorMessage(rawResponse: string, status: number): string {
    let message = `Request failed with status ${status}.`;

    try {
        const payload = JSON.parse(rawResponse) as { error?: string };

        if (payload.error) {
            message = payload.error;
        }
    } catch {
        if (rawResponse.trim()) {
            message = rawResponse.trim();
        }
    }

    return message;
}

async function readJsonResponse<T>(
    response: Response,
    method: string,
): Promise<EeCheckApiResult<T>> {
    const rawResponse = await response.text();

    if (!response.ok) {
        throw new EeCheckApiError(
            extractErrorMessage(rawResponse, response.status),
            response.status,
            response.url,
            rawResponse,
        );
    }

    try {
        return {
            data: JSON.parse(rawResponse) as T,
            meta: {
                method,
                rawResponse,
                status: response.status,
                url: response.url,
            },
        };
    } catch {
        throw new EeCheckApiError(
            'Response could not be parsed as JSON.',
            response.status,
            response.url,
            rawResponse,
        );
    }
}

async function readTextResponse(
    response: Response,
    method: string,
): Promise<EeCheckApiResult<string>> {
    const rawResponse = await response.text();

    if (!response.ok) {
        throw new EeCheckApiError(
            extractErrorMessage(rawResponse, response.status),
            response.status,
            response.url,
            rawResponse,
        );
    }

    return {
        data: rawResponse,
        meta: {
            method,
            rawResponse,
            status: response.status,
            url: response.url,
        },
    };
}

/**
 * The only API transport entry point used by the application. It keeps the
 * development proxy protocol separate from EE-Check's direct HTTP contract.
 */
export class EeCheckApiClient {
    private readonly usesInternalApiProxy = IS_INTERNAL_API_PROXY_ENABLED;

    public getOriginalProxyRequest(
        url: string,
        parameters: unknown,
    ): EeCheckApiOriginalRequest | null {
        if (!this.usesInternalApiProxy) {
            return null;
        }

        const pathname = new URL(url, EE_CHECK_API_ORIGIN).pathname;

        switch (pathname) {
            case INTERNAL_TOKEN_LOGIN_URL:
                return {
                    method: 'POST',
                    parameters,
                    url: `${EE_CHECK_API_ORIGIN}${DIRECT_TOKEN_LOGIN_PATH}`,
                };
            case INTERNAL_STORED_PLOTLY_URL:
                return {
                    headers: { Authorization: 'Bearer [redacted]' },
                    method: 'POST',
                    parameters,
                    url: `${EE_CHECK_API_ORIGIN}${DIRECT_STORED_PLOTLY_PATH}`,
                };
            case INTERNAL_STORED_CHARTJS_URL: {
                const payload = parameters as StoredChartJsRequest;

                return {
                    headers: { Authorization: 'Bearer [redacted]' },
                    method: 'GET',
                    parameters: {
                        definition_name: payload.definitionName,
                        ignore_class_check: 1,
                    },
                    url: `${EE_CHECK_API_ORIGIN}${this.buildDirectStoredChartJsPath(payload)}`,
                };
            }
            case INTERNAL_TIME_SERIES_URL:
                return {
                    headers: { Authorization: 'Bearer [redacted]' },
                    method: 'GET',
                    parameters: null,
                    url: `${EE_CHECK_API_ORIGIN}${this.buildDirectTimeSeriesPath(parameters as TimeSeriesRequest)}`,
                };
            case INTERNAL_LAST_VALUE_URL: {
                const payload = parameters as LastValueRequest;

                return {
                    headers: { Authorization: 'Bearer [redacted]' },
                    method: 'POST',
                    parameters: this.buildDirectLastValueRequestBody(payload),
                    url: `${EE_CHECK_API_ORIGIN}${DIRECT_LAST_VALUE_PATH}`,
                };
            }
            case INTERNAL_INTERVAL_VALUE_URL:
                return {
                    headers: { Authorization: 'Bearer [redacted]' },
                    method: 'GET',
                    parameters: null,
                    url: `${EE_CHECK_API_ORIGIN}${this.buildDirectIntervalValuePath(parameters as IntervalValueRequest)}`,
                };
            case INTERNAL_MUENSTER_TIME_SERIES_URL:
                return {
                    method: 'GET',
                    parameters: null,
                    url: String((parameters as { url?: unknown } | undefined)?.url ?? ''),
                };
            case INTERNAL_CONFIGURATION_READ_URL:
                return {
                    headers: { Authorization: 'Bearer [redacted]' },
                    method: 'POST',
                    parameters,
                    url: `${EE_CHECK_API_ORIGIN}${DIRECT_CONFIGURATION_READ_PATH}`,
                };
            case INTERNAL_CONFIGURATION_WRITE_URL:
                return {
                    headers: { Authorization: 'Bearer [redacted]' },
                    method: 'POST',
                    parameters,
                    url: `${EE_CHECK_API_ORIGIN}${DIRECT_CONFIGURATION_WRITE_PATH}`,
                };
            case INTERNAL_BUILDING_STATISTICS_URL: {
                const system = String((parameters as { system?: unknown } | undefined)?.system ?? '');

                return {
                    headers: { Authorization: 'Bearer [redacted]' },
                    method: 'GET',
                    parameters: null,
                    url: `${EE_CHECK_API_ORIGIN}${this.buildDirectBuildingStatisticsPath(system)}`,
                };
            }
            default:
                return null;
        }
    }

    public async login(
        credentials?: ApiTokenCredentials,
    ): Promise<EeCheckApiResult<ApiTokenResponse>> {
        const effectiveCredentials = getEffectiveCredentials(credentials);

        if (!effectiveCredentials) {
            throw new EeCheckApiError(
                'Credentials are required to acquire an API token.',
                400,
                this.usesInternalApiProxy
                    ? INTERNAL_TOKEN_LOGIN_URL
                    : this.getDirectApiUrl(DIRECT_TOKEN_LOGIN_PATH),
                '',
            );
        }

        if (this.usesInternalApiProxy) {
            const response = await fetch(INTERNAL_TOKEN_LOGIN_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(effectiveCredentials),
            });

            return readJsonResponse<ApiTokenResponse>(response, 'POST');
        }

        const requestBody = new URLSearchParams({
            password: effectiveCredentials.password,
            username: effectiveCredentials.username,
        }).toString();
        const loginResponse = await fetch(this.getDirectApiUrl(DIRECT_TOKEN_LOGIN_PATH), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: requestBody,
        });
        const result = await readTextResponse(loginResponse, 'POST');

        return {
            data: { token: result.data.trim() },
            meta: result.meta,
        };
    }

    public async requestStoredPlotly(
        accessToken: string,
        payload: StoredPlotlyRequest,
        onProgress?: (progress: EeCheckApiRequestProgress) => void,
    ): Promise<EeCheckApiResult<StoredPlotlyResponse>> {
        const url = this.usesInternalApiProxy
            ? INTERNAL_STORED_PLOTLY_URL
            : this.getDirectApiUrl(DIRECT_STORED_PLOTLY_PATH);
        const cachePayload = { accessToken, payload };
        const cacheKey = buildEeCheckApiCacheKey(url, cachePayload);
        const cachedResult = readEeCheckApiCache<EeCheckApiResult<StoredPlotlyResponse>>(cacheKey);

        if (cachedResult) {
            return cachedResult;
        }

        onProgress?.({ currentRequest: 1, totalRequests: 1 });

        return getOrCreateEeCheckApiInFlightRequest(cacheKey, async () => {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    ...(!this.usesInternalApiProxy ? this.getAuthorizationHeaders(accessToken) : {}),
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(this.usesInternalApiProxy ? cachePayload : payload),
                ...(this.usesInternalApiProxy ? { credentials: 'same-origin' as const } : {}),
            });
            const result = await readJsonResponse<StoredPlotlyResponse>(response, 'POST');

            if (getSerializedSizeBytes(result) <= MAX_PLOTLY_CACHE_SIZE_BYTES) {
                writeEeCheckApiCache(cacheKey, result);
            }

            return result;
        });
    }

    public async requestStoredChartJs(
        accessToken: string,
        payload: StoredChartJsRequest,
    ): Promise<EeCheckApiResult<StoredChartJsResponse>> {
        const url = this.usesInternalApiProxy
            ? INTERNAL_STORED_CHARTJS_URL
            : this.getDirectApiUrl(this.buildDirectStoredChartJsPath(payload));
        const cachePayload = { accessToken, payload };
        const cacheKey = buildEeCheckApiCacheKey(url, cachePayload);
        const cachedResult = readEeCheckApiCache<EeCheckApiResult<StoredChartJsResponse>>(cacheKey);

        if (cachedResult) {
            return cachedResult;
        }

        return getOrCreateEeCheckApiInFlightRequest(cacheKey, async () => {
            const response = await fetch(
                url,
                this.usesInternalApiProxy
                    ? {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ accessToken, payload }),
                        credentials: 'same-origin',
                    }
                    : {
                        method: 'GET',
                        headers: this.getAuthorizationHeaders(accessToken),
                    },
            );
            const result = await readJsonResponse<StoredChartJsResponse>(
                response,
                this.usesInternalApiProxy ? 'POST' : 'GET',
            );

            if (getSerializedSizeBytes(result) <= MAX_PLOTLY_CACHE_SIZE_BYTES) {
                writeEeCheckApiCache(cacheKey, result);
            }

            return result;
        });
    }

    public async requestTimeSeries(
        accessToken: string,
        payload: TimeSeriesRequest,
        onProgress?: (progress: EeCheckApiRequestProgress) => void,
    ): Promise<EeCheckApiResult<TimeSeriesResponse>> {
        const requestUrl = this.usesInternalApiProxy
            ? INTERNAL_TIME_SERIES_URL
            : this.buildDirectTimeSeriesUrl(payload);
        const cachePayload = { accessToken, payload };
        const cacheKey = buildEeCheckApiCacheKey(requestUrl, cachePayload);
        const cachedResult = readEeCheckApiCache<EeCheckApiResult<TimeSeriesResponse>>(cacheKey);

        if (cachedResult) {
            return cachedResult;
        }

        return getOrCreateEeCheckApiInFlightRequest(cacheKey, async () => {
            const urnBatches = splitUrnBatches(payload.urn, MAX_TIME_SERIES_URN_BATCH_BYTES);
            const batchResults: EeCheckApiResult<TimeSeriesResponse>[] = [];

            for (const [index, chunkUrn] of urnBatches.entries()) {
                batchResults.push(await this.requestTimeSeriesChunk(
                    accessToken,
                    { ...payload, urn: chunkUrn },
                    index + 1,
                    urnBatches.length,
                    onProgress,
                ));
            }

            const result = batchResults.length === 1
                ? batchResults[0]
                : this.mergeTimeSeriesBatchResults(batchResults, requestUrl);

            writeEeCheckApiCache(cacheKey, result);

            return result;
        });
    }

    public async requestCurrentValues(
        accessToken: string,
        payload: LastValueRequest,
        onProgress?: (progress: EeCheckApiRequestProgress) => void,
    ): Promise<EeCheckApiResult<LastValueResponse>> {
        const requestUrl = this.usesInternalApiProxy
            ? INTERNAL_LAST_VALUE_URL
            : this.getDirectApiUrl(DIRECT_LAST_VALUE_PATH);
        const cachePayload = {
            ...LAST_VALUE_REQUEST_OPTIONS,
            accessToken,
            payload,
        };
        const cacheKey = buildEeCheckApiCacheKey(requestUrl, cachePayload);
        const cachedResult = readEeCheckApiCache<EeCheckApiResult<LastValueResponse>>(cacheKey);

        if (cachedResult) {
            return cachedResult;
        }

        onProgress?.({ currentRequest: 1, totalRequests: 1 });

        return getOrCreateEeCheckApiInFlightRequest(cacheKey, async () => {
            const response = await fetch(
                requestUrl,
                this.usesInternalApiProxy
                    ? {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ accessToken, payload }),
                        credentials: 'same-origin',
                    }
                    : {
                        method: 'POST',
                        headers: {
                            ...this.getAuthorizationHeaders(accessToken),
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(this.buildDirectLastValueRequestBody(payload)),
                    },
            );
            const result = this.usesInternalApiProxy
                ? await readJsonResponse<LastValueResponse>(response, 'POST')
                : await readTextResponse(response, 'POST');

            writeEeCheckApiCache(cacheKey, result);

            return result;
        });
    }

    public async requestIntervalValues(
        accessToken: string,
        payload: IntervalValueRequest,
        onProgress?: (progress: EeCheckApiRequestProgress) => void,
    ): Promise<EeCheckApiResult<IntervalValueResponse>> {
        const requestUrl = this.usesInternalApiProxy
            ? INTERNAL_INTERVAL_VALUE_URL
            : this.buildDirectIntervalValueUrl(payload);
        const cachePayload = { accessToken, payload };
        const cacheKey = buildEeCheckApiCacheKey(requestUrl, cachePayload);
        const cachedResult = readEeCheckApiCache<EeCheckApiResult<IntervalValueResponse>>(cacheKey);

        if (cachedResult) {
            return cachedResult;
        }

        onProgress?.({ currentRequest: 1, totalRequests: 1 });

        return getOrCreateEeCheckApiInFlightRequest(cacheKey, async () => {
            const response = await fetch(
                requestUrl,
                this.usesInternalApiProxy
                    ? {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ accessToken, payload }),
                        credentials: 'same-origin',
                    }
                    : {
                        method: 'GET',
                        headers: this.getAuthorizationHeaders(accessToken),
                    },
            );
            const result = this.usesInternalApiProxy
                ? await readJsonResponse<IntervalValueResponse>(response, 'POST')
                : await readTextResponse(response, 'GET');

            writeEeCheckApiCache(cacheKey, result);

            return result;
        });
    }

    public async requestMuensterTimeSeries(url: string): Promise<EeCheckApiResult<string>> {
        const response = await fetch(
            this.usesInternalApiProxy ? INTERNAL_MUENSTER_TIME_SERIES_URL : url,
            this.usesInternalApiProxy
                ? {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ payload: { url } }),
                    credentials: 'same-origin',
                }
                : {
                    method: 'GET',
                    credentials: 'omit',
                },
        );

        return this.usesInternalApiProxy
            ? readJsonResponse<string>(response, 'POST')
            : readTextResponse(response, 'GET');
    }

    public async readConfiguration(
        accessToken: string,
        tags: ConfigurationTags,
    ): Promise<EeCheckApiResult<ConfigurationReadResponse>> {
        const url = this.usesInternalApiProxy
            ? INTERNAL_CONFIGURATION_READ_URL
            : this.getDirectApiUrl(DIRECT_CONFIGURATION_READ_PATH);
        const payload = { tags };
        const response = await fetch(
            url,
            this.usesInternalApiProxy
                ? {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ accessToken, payload }),
                    credentials: 'same-origin',
                }
                : {
                    method: 'POST',
                    headers: {
                        ...this.getAuthorizationHeaders(accessToken),
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                },
        );

        return readJsonResponse<ConfigurationReadResponse>(response, 'POST');
    }

    public async saveConfiguration(
        accessToken: string,
        tags: ConfigurationTags,
        content: string,
    ): Promise<EeCheckApiResult<ConfigurationWriteResponse>> {
        const url = this.usesInternalApiProxy
            ? INTERNAL_CONFIGURATION_WRITE_URL
            : this.getDirectApiUrl(DIRECT_CONFIGURATION_WRITE_PATH);
        const payload = {
            content,
            data_type: 'array',
            tags,
        };
        const response = await fetch(
            url,
            this.usesInternalApiProxy
                ? {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ accessToken, payload }),
                    credentials: 'same-origin',
                }
                : {
                    method: 'POST',
                    headers: {
                        ...this.getAuthorizationHeaders(accessToken),
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                },
        );

        return readJsonResponse<ConfigurationWriteResponse>(response, 'POST');
    }

    public async requestBuildingStatistics(
        accessToken: string,
        system: string,
    ): Promise<EeCheckApiResult<BuildingStatisticsResponse>> {
        const directPath = this.buildDirectBuildingStatisticsPath(system);
        const url = this.usesInternalApiProxy
            ? INTERNAL_BUILDING_STATISTICS_URL
            : this.getDirectApiUrl(directPath);
        const response = await fetch(
            url,
            this.usesInternalApiProxy
                ? {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ accessToken, payload: { system } }),
                    credentials: 'same-origin',
                }
                : {
                    method: 'GET',
                    headers: this.getAuthorizationHeaders(accessToken),
                },
        );

        return readJsonResponse<BuildingStatisticsResponse>(
            response,
            this.usesInternalApiProxy ? 'POST' : 'GET',
        );
    }

    private getDirectApiUrl(path: string): string {
        return `${DIRECT_API_ORIGIN}${path}`;
    }

    private getAuthorizationHeaders(accessToken: string): Record<string, string> {
        return {
            Authorization: `Bearer ${accessToken}`,
        };
    }

    private buildDirectTimeSeriesPath(payload: TimeSeriesRequest): string {
        const encodedFrom = encodeURIComponent(payload.from);
        const encodedTo = encodeURIComponent(payload.to);
        const encodedUrn = encodeEeCheckTimeSeriesUrn(payload.urn);

        return `${DIRECT_TIME_SERIES_PATH_PREFIX}/${encodedFrom}/${encodedTo}/changeofvalue/${encodedUrn}`;
    }

    private buildDirectStoredChartJsPath(payload: StoredChartJsRequest): string {
        const query = new URLSearchParams({
            definition_name: payload.definitionName,
            ignore_class_check: '1',
        });

        return `${DIRECT_STORED_CHARTJS_PATH}?${query.toString()}`;
    }

    private buildDirectBuildingStatisticsPath(system: string): string {
        return `${DIRECT_BUILDING_STATISTICS_PATH_PREFIX}/${encodeURIComponent(system)}`;
    }

    private buildDirectTimeSeriesUrl(payload: TimeSeriesRequest): string {
        return this.getDirectApiUrl(this.buildDirectTimeSeriesPath(payload));
    }

    private buildDirectIntervalValuePath(payload: IntervalValueRequest): string {
        const encodedFrom = encodeURIComponent(payload.from);
        const encodedTo = encodeURIComponent(payload.to);
        const encodedInterval = encodeURIComponent(payload.interval ?? '1year');
        const encodedUrn = encodeEeCheckTimeSeriesUrn(payload.urn);

        return `${DIRECT_INTERVAL_VALUE_PATH_PREFIX}/${encodedFrom}/${encodedTo}/${encodedInterval}/${encodedUrn}`;
    }

    private buildDirectIntervalValueUrl(payload: IntervalValueRequest): string {
        return this.getDirectApiUrl(this.buildDirectIntervalValuePath(payload));
    }

    private buildDirectLastValueRequestBody(payload: LastValueRequest): Record<string, boolean | string> {
        return {
            urns: payload.urn,
            ...(payload.timestamp !== undefined ? { timestamp: payload.timestamp } : {}),
            ...LAST_VALUE_REQUEST_OPTIONS,
        };
    }

    private async requestTimeSeriesChunk(
        accessToken: string,
        payload: TimeSeriesRequest,
        currentRequest: number,
        totalRequests: number,
        onProgress?: (progress: EeCheckApiRequestProgress) => void,
    ): Promise<EeCheckApiResult<TimeSeriesResponse>> {
        const chunkPayload = { accessToken, payload };
        const chunkUrl = this.usesInternalApiProxy
            ? INTERNAL_TIME_SERIES_URL
            : this.buildDirectTimeSeriesUrl(payload);
        const chunkCacheKey = buildEeCheckApiCacheKey(chunkUrl, chunkPayload);
        const cachedChunkResult = readEeCheckApiCache<EeCheckApiResult<TimeSeriesResponse>>(chunkCacheKey);

        if (cachedChunkResult) {
            return cachedChunkResult;
        }

        onProgress?.({ currentRequest, totalRequests });

        const response = await fetch(
            chunkUrl,
            this.usesInternalApiProxy
                ? {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(chunkPayload),
                    credentials: 'same-origin',
                }
                : {
                    method: 'GET',
                    headers: this.getAuthorizationHeaders(accessToken),
                },
        );
        const result = this.usesInternalApiProxy
            ? await readJsonResponse<TimeSeriesResponse>(response, 'POST')
            : await readTextResponse(response, 'GET');

        writeEeCheckApiCache(chunkCacheKey, result);

        return result;
    }

    private mergeTimeSeriesBatchResults(
        batchResults: EeCheckApiResult<TimeSeriesResponse>[],
        requestUrl: string,
    ): EeCheckApiResult<TimeSeriesResponse> {
        const data = mergeTimeSeriesCsvResponses(
            batchResults
                .map((batchResult) => batchResult.data)
                .filter((result): result is string => typeof result === 'string'),
        );

        return {
            data,
            meta: {
                method: this.usesInternalApiProxy ? 'POST' : 'GET',
                rawResponse: JSON.stringify(data),
                status: 200,
                url: requestUrl,
            },
        };
    }
}

export const eeCheckApi = new EeCheckApiClient();

/**
 * Backwards-compatible exports for components that still use the former
 * function API. All requests are delegated to the central API client.
 */
export function getOriginalEeCheckProxyRequest(
    url: string,
    parameters: unknown,
): EeCheckApiOriginalRequest | null {
    return eeCheckApi.getOriginalProxyRequest(url, parameters);
}

export function requestApiToken(
    credentials?: ApiTokenCredentials,
): Promise<EeCheckApiResult<ApiTokenResponse>> {
    return eeCheckApi.login(credentials);
}

export function requestExampleDataset(
    accessToken: string,
    payload: StoredPlotlyRequest,
    onProgress?: (progress: EeCheckApiRequestProgress) => void,
): Promise<EeCheckApiResult<StoredPlotlyResponse>> {
    return eeCheckApi.requestStoredPlotly(accessToken, payload, onProgress);
}

export function requestStoredChartJsDataset(
    accessToken: string,
    payload: StoredChartJsRequest,
): Promise<EeCheckApiResult<StoredChartJsResponse>> {
    return eeCheckApi.requestStoredChartJs(accessToken, payload);
}

export function requestTimeSeriesDataset(
    accessToken: string,
    payload: TimeSeriesRequest,
    onProgress?: (progress: EeCheckApiRequestProgress) => void,
): Promise<EeCheckApiResult<TimeSeriesResponse>> {
    return eeCheckApi.requestTimeSeries(accessToken, payload, onProgress);
}

export function requestCurrentValueDataset(
    accessToken: string,
    payload: LastValueRequest,
    onProgress?: (progress: EeCheckApiRequestProgress) => void,
): Promise<EeCheckApiResult<LastValueResponse>> {
    return eeCheckApi.requestCurrentValues(accessToken, payload, onProgress);
}

export function requestIntervalValueDataset(
    accessToken: string,
    payload: IntervalValueRequest,
    onProgress?: (progress: EeCheckApiRequestProgress) => void,
): Promise<EeCheckApiResult<IntervalValueResponse>> {
    return eeCheckApi.requestIntervalValues(accessToken, payload, onProgress);
}
